import {IconButton, ToggleButton} from '@wandb/components';
import clsx from 'clsx';
import React from 'react';

import {useRampFlagRunSelectorPinOnHover} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import type {WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {usePinningActions} from '../hooks/usePinningActions';

type RowPinningProps = {
  isSingleMode: boolean;
  isVisible: boolean;
  row: WBTableRowWithRunSetInfo;
};

export function RowPinning({isSingleMode, isVisible, row}: RowPinningProps) {
  const showPinningControlInline = useRampFlagRunSelectorPinOnHover();
  const {
    baselineLabel,
    canBaselineRun,
    canPinRun,
    canUnpinRun,
    isBaseline,
    isPinned,
    onBaselineClick,
    onPinClick,
    pinLabel,
  } = usePinningActions({row});

  if (isSingleMode) {
    return null;
  }

  if (isBaseline && canBaselineRun) {
    return (
      <ToggleButton
        variant="bookmark"
        isSelected
        onPress={onBaselineClick}
        aria-label={baselineLabel}
        tooltip={{children: baselineLabel}}
        size="md"
      />
    );
  }
  if (isPinned && (canPinRun || canUnpinRun)) {
    return (
      <ToggleButton
        variant="pin"
        isSelected
        onPress={onPinClick}
        aria-label={pinLabel}
        tooltip={{children: pinLabel}}
        size="md"
        isDisabled={!canPinRun && !isPinned}
      />
    );
  }

  // Experiment: Show pinning control for every row, even if not pinned. Gated
  // by the feature flag, which will have a random sampling of users within it.
  // This is a separate if-block to make it easy to remove if we decide this ui
  // component is too noisy. If not, TODO: consolidate with the above if-block
  if (showPinningControlInline && canPinRun) {
    // Wrapper with class here to mimic the behavior of the run row menu.
    const cls = clsx(
      'flex flex-col items-center',
      'transition-opacity duration-300',
      {'opacity-0': !isVisible}
    );

    return (
      <div className={cls}>
        <IconButton
          icon="pin"
          onPress={onPinClick}
          aria-label={pinLabel}
          tooltip={{children: pinLabel}}
          size="sm"
          variant="ghost"
        />
      </div>
    );
  }

  return null;
}
